import { queryClient } from '@/router'
import { useMutation } from '@tanstack/react-query'
import { Trash2Icon } from 'lucide-react'
import { toast } from 'sonner'

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/alert-dialog/alert-dialog'
import { Button } from '@/components/button/button'
import { Input } from '@/components/input/input'
import { Label } from '@/components/label/label'

import { API_ENDPOINTS } from '@/lib/constants/api'
import { useBooleanState } from '@/lib/hooks/use-boolean-state'
import { deleteTaskActionMutation } from '@/lib/queries/business/v1/task/action/mutations'
import { cn } from '@/lib/utils'

export interface DeleteTaskActionDialogTriggerProps {
  taskId: number
  actionId: number
  actionName: string
}

export const DeleteTaskActionDialogTrigger = ({ taskId, actionId, actionName }: DeleteTaskActionDialogTriggerProps) => {
  const {
    booleanState: isOpenDeleteTaskActionDialog,
    setToTrue: openDeleteTaskActionDialog,
    setToFalse: closeDeleteTaskActionDialog,
  } = useBooleanState(false)

  const { mutateAsync: deleteTaskAction, isPending: isDeletingTaskAction } = useMutation(deleteTaskActionMutation)

  const isDisabledCancelButton = isDeletingTaskAction
  const isLoadingDeleteButton = isDeletingTaskAction

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      closeDeleteTaskActionDialog()
    }
  }

  const handleClickDeleteButton = async () => {
    await deleteTaskAction(
      { taskId, actionId },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: [API_ENDPOINTS.business.v1.task.action.index(taskId)],
          })
          toast.success('Subtask has been deleted!')
        },
        onError: () => {
          toast.error('Failed to delete subtask. Please try again later.')
        },
      }
    )
  }

  return (
    <>
      <button type="button" className={cn('w-4', 'h-4', 'ml-auto')} onClick={openDeleteTaskActionDialog}>
        <Trash2Icon size={16} />
      </button>
      <AlertDialog open={isOpenDeleteTaskActionDialog} onOpenChange={handleOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Subtask</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this subtask? It will not be visible to other members.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className={cn('flex flex-col gap-y-2')}>
            <Label>Subtask name</Label>
            <Input
              disabled
              value={actionName}
              className={cn('border-none', 'bg-slate-50', 'text-sm !text-foreground')}
            />
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDisabledCancelButton}>Cancel</AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              loading={isLoadingDeleteButton}
              onClick={handleClickDeleteButton}
            >
              Delete
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
